import {
  ChipBadge,
  formatErrorMessages,
  IStyles,
  IconWrapper,
  Loader,
  MobilePopup,
  NoContentCard,
  QuantityButton,
  QuestionContainerWithSolution,
  deserify,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getHumanReadableTimestampString,
  getMediaBasePath,
  getTeacherSubjectName,
  pxToRem,
  pxTovW,
  theme,
  uploadFile,
  useCommonServiceClientContext,
  getDeviceType,
  downloadPdfToAndroid,
  formatSecondsToDateTimeString,
  QuestionSourceTags,
} from '@geneo2-web/shared-ui';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CloudUpload,
  QuestionModuleMap,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  bloomLevelValuesArray,
  difficultyValuesArray,
  toBloomLevelEnum,
  toDifficultyEnum,
  toQuestionEnum,
  toStringQuestion,
  getAvailableQuestionTypes,
} from '../../../../utils/functions';
import { useTextBookQuestionEnabled } from '../../../../utils/customHooks';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../reduxStore/reduxHooks';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { AssessmentCreationEnum } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { Question } from '@protos/content_management/content.db_pb';
import {
  numberToAlphabet,
  useAddOrReplaceQPGQuestion,
} from '../ReviewQuestionPaper/utils';
import { EditWarningPopup } from '../../../../components/EditWarningPopup';

import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../Auth/auth.events';
import {
  setQPGSearchQuestionsConfig,
  QuestionSourceFilter,
  setQuestionUploadFilter,
} from '../../reducer/assessmentFlow.slice';
import { AddPromptPopup } from './components/AddPromptPopup';
import { getDifficultyLevelFromString } from '../../Common/ReviewAssessment/functions';
import { ValidationPopup } from '../QuestionPaperCreate/components/ValidationPopup';
import AiGeneratedButton from '../ReviewQuestionPaper/Buttons/AiGeneratedButton';
import { ConnectError } from '@bufbuild/connect';
import {
  CustomUploadFileResponse,
  setQPGQuestionModuleMap,
} from '../../reducer/assessmentFlow.slice';
import { AddQuestionsPopup } from '../../components/UploadQuestions/AddQuestionPopup';
import { setToastInfoV2 } from '../../../Home/reducer/homeDashboard.slice';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  FileUploadPurpose,
  MimeEnum,
  SignedUrlReponse,
} from '@protos/learning_management/lms.common.apis_pb';
import { APIResponse_StatusEnum } from '@protos/common/common.db_pb';
import { Timestamp } from '@bufbuild/protobuf';
import styled from '@emotion/styled';
import { UploadValidationError } from '@protos/content_management/cms.teacher.questions.apis_pb';

const styles: IStyles = {
  root: {
    bgcolor: '#F8F8FC',
    boxSizing: 'border-box',
    // overflow: { md: 'hidden', xs: 'auto' },
    px: { xs: pxToRem(20), lg: pxTovW(240) },
  },
  mainContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxTovW(40) },
    overflow: 'scroll',
    height: '100%',
  },
  leftPanel: {
    flexBasis: { md: pxTovW(550), lg: pxTovW(615) },
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(30) },
  },
  rightPanel: {
    flexBasis: { md: pxTovW(1000) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    mb: { xs: pxToRem(20), md: pxTovW(80) },
    overflow: { xs: 'initial', md: 'scroll' },
    flexGrow: 1,
    background: '#fff',
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    boxShadow: '0px 0px 10px #E7E7E74A',
    p: { xs: pxToRem(10), md: pxTovW(20) },
    display: 'flex',
    flexDirection: 'column',
    // alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxTovW(30) },
    maxHeight: { md: '70vh' },
    position: 'relative',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(20) },
    // padding: {
    //   md: `${pxTovW(30)} ${pxTovW(40)}`,
    // },
    // bgcolor: 'common.white',
  },
  heading: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    // borderBottom: { md: '1px solid grey' },
    py: { xs: pxToRem(20), md: pxTovW(30) },
  },
  textBox: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: { xs: pxToRem(10), md: pxTovW(0) },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    '& > * ': { display: 'inline' },
  },
  questionBank: {
    // bgcolor: 'common.white',
    padding: { md: pxTovW(30) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    borderRadius: { xs: pxToRem(30), md: pxTovW(30) },
    // border: '1px solid red',
    // overflowY: 'scroll',
  },
  selectContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
};
interface IUploadStatusMessage {
  errors?: UploadValidationError[] | string[];
  fileName?: string;
  successMessage?: string;
}

export default function SearchQuestions() {
  const [searchText, setSearchText] = useState<string>('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { setSelectedFunction } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const dispatch = useAppDispatch();
  const fromPath = location.state?.from;
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const isTextBookQuestionEnabled = useTextBookQuestionEnabled();
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const {
    qpg_question_module_map,
    qpg_settings_config,
    chapter_topic_data,
    qpg_selected_chapter_data,
    qpg_search_questions_config,
    current_qpg_template_info,
  } = deserify(useAppSelector((state) => state.assessment));
  const [openFileUploadPopup, setOpenFileUploadPopup] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [searchQuestionModuleMap, setSearchQuestionModuleMap] = useState<
    QuestionModuleMap[]
  >([]);
  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const MAX_FILE_LIMIT_MB = 50;
  const {
    qpg_assessment_task,
    qpg_questions_sequence_info,
    qpg_assessment_content,
    selected_question_index_for_alternate,
    selected_section_index_for_alternate,
    selected_question_type_for_alternate,
    question_upload_filter,
  } = deserify(useAppSelector((state) => state.assessment));
  const isAlternateQuestion =
    typeof selected_question_index_for_alternate == 'number' &&
    typeof selected_section_index_for_alternate == 'number';
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const questionSectionMetaInfo =
    qpgQuestionsSequenceInfo?.questionSectionMetaInfo || [];
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const qids =
    questionSectionMetaInfo
      ?.map((e) =>
        e.sectionQuestionsInfo?.map((e) => [
          e?.questionId,
          ...(e?.alternateQuestions
            .sort((a, b) => a.sequence - b.sequence)
            .map((altQuestion) => altQuestion.questionId) || []),
        ])
      )
      .flat()
      .flat() || [];
  const {
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
    TeacherUploadQuestionV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV2ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [difficultyModalState, setDifficultyModalState] = useState(false);
  const [quesTypeModalState, setQuesTypeModalState] = useState(false);
  const [bloomLevelModalState, setBloomLevelModalState] = useState(false);
  const [chapterModalState, setChapterModalState] = useState(false);
  const [topicModalState, setTopicModalState] = useState(false);
  const [validationStatus, setValidationStatus] = useState<
    { type: 'success' | 'error'; heading?: string; message: string } | undefined
  >(undefined);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(
    null
  );
  const [FileUploading, setFileUploading] = useState(false);
  const [File, setFile] = useState<CustomUploadFileResponse | undefined>();
  const [uploadStatusMessage, setUploadStatusMessage] =
    useState<IUploadStatusMessage>({});
  const [selectedBloomLevel, setSelectedBloomLevel] = useState<string | null>(
    null
  );
  const [selectedQuestionType, setSelectedQuestionType] = useState<
    string | null
  >(
    (selected_question_type_for_alternate
      ? toStringQuestion(selected_question_type_for_alternate)
      : null) || null
  );
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(
    null
  );
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 10;

  const selectedBloomLevelEnum = useMemo(() => {
    return selectedBloomLevel ? toBloomLevelEnum(selectedBloomLevel) : null;
  }, [selectedBloomLevel]);

  const selectedQuestionTypeEnum = useMemo(() => {
    return selectedQuestionType ? toQuestionEnum(selectedQuestionType) : null;
  }, [selectedQuestionType]);

  const selectedDifficultyEnum = useMemo(() => {
    return selectedDifficulty ? toDifficultyEnum(selectedDifficulty) : null;
  }, [selectedDifficulty]);

  const availableQuestionTypes = useMemo(() => {
    return getAvailableQuestionTypes(qpg_settings_config?.allow_ai_questions);
  }, [qpg_settings_config?.allow_ai_questions]);

  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);

  const [popupState, setPopupState] = useState<boolean>(false);
  const [addPromptPopupOpen, setAddPromptPopupOpen] = useState<boolean>(false);
  const [popupDetails, setPopupDetails] = useState<{
    title?: string;
    yesHandler?: () => void;
    yesToast?: string;
  }>({});
  const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);
  const leftPanelScrollTopRef = useRef<HTMLDivElement | null>(null);
  const rightPanelScrollTopRef = useRef<HTMLDivElement | null>(null);
  const moduleIds =
    qpg_selected_chapter_data?.chapters
      .flatMap((chapter) => [
        chapter.chapterId,
        ...chapter.topics.map((topic) => topic.topicId),
      ])
      ?.sort((a, b) => a - b) || [];

  const qpgAssessmentTask = new AssessmentTask(qpg_assessment_task);
  const addOrReplaceQuestion = useAddOrReplaceQPGQuestion();
  // Chapter and topic selection logic (refactored for conciseness)
  const chapters =
    qpgAssessmentTask?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo ?? [];
  const chapterOptions = chapters.map(
    ({ chapterNo, name, chapterId, topicInfo }) => ({
      label: `${chapterNo}. ${name}`,
      value: name,
      chapterId,
      topicInfo: topicInfo ?? [],
    })
  );
  const selectedChapter =
    chapterOptions.find((c) => c.chapterId === selectedChapterId) || null;
  const topicOptions = (
    selectedChapter
      ? selectedChapter.topicInfo
      : chapters.flatMap((c) => c.topicInfo ?? [])
  ).map(({ name, topicId }) => ({ label: name, value: name, topicId }));

  const resetTopicId = useCallback(
    (chapterId: number | null) => {
      if (chapterId) {
        if (selectedTopicId) {
          const topic = topicOptions.find((t) => t.topicId === selectedTopicId);
          if (!topic) {
            setSelectedTopicId(null);
          }
        }
      }
    },
    [selectedTopicId, topicOptions]
  );

  const fetchQuestions = async (
    prompt?: string,
    userFilter?: QuestionSourceFilter
  ) => {
    try {
      const moduleIds = selectedTopicId
        ? [selectedTopicId]
        : selectedChapterId
        ? [
            selectedChapterId,
            ...(chapterOptions
              ?.find((c) => c.chapterId === selectedChapterId)
              ?.topicInfo?.map((t) => t.topicId) || []),
          ]
        : chapterOptions
            ?.flatMap((chapter) => [
              chapter.chapterId,
              ...chapter.topicInfo.map((topic) => topic.topicId),
            ])
            ?.sort((a, b) => a - b) || [];
      setLoading('loading');
      const effectiveFilter = userFilter ?? question_upload_filter?.value;
      const response =
        await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
          {
            teacherId: user_info?.teacherProfileId,
            createdByUserId:
              effectiveFilter === 'teacher'
                ? user_info?.teacherProfileId
                : undefined,
            subjectId: qpg_assessment_task?.subjectId,
            schoolId:
              effectiveFilter === 'teacher'
                ? user_info?.schoolDetails[0]?.schoolId
                : undefined,
            textbookQuestions:
              effectiveFilter === 'text-book-questions' ? true : undefined,
            moduleIds: moduleIds,
            qIdsToBeIgnored: qids,
            aiQuestionPrompts: [
              prompt || qpg_search_questions_config?.prompt || '',
            ],
            noOfQuestions: qpg_search_questions_config?.prompt_validated
              ? 10
              : undefined,
            isAiAssessment:
              qpg_settings_config?.allow_ai_questions &&
              qpg_search_questions_config?.prompt_validated === 'success',
            requiredQuestionTypes: selectedQuestionTypeEnum
              ? [selectedQuestionTypeEnum]
              : [],
            requiredDifficultyTypes: selectedDifficultyEnum
              ? [selectedDifficultyEnum]
              : [],
            bloomLevels: selectedBloomLevelEnum ? [selectedBloomLevelEnum] : [],
            assessmentCreationType:
              AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
          }
        );
      const quesList =
        qpg_settings_config?.allow_ai_questions &&
        qpg_search_questions_config?.prompt_validated === 'success'
          ? [...(response.data?.questions || []), ...(questionList || [])]
          : response.data?.questions || [];
      console.log('Fetched Questions:', quesList);
      const newSearchQuestionModuleMap =
        qpg_settings_config?.allow_ai_questions &&
        qpg_search_questions_config?.prompt_validated === 'success'
          ? [
              ...(response.data?.questionModuleMap || []),
              ...searchQuestionModuleMap,
            ]
          : response.data?.questionModuleMap || [];
      setSearchQuestionModuleMap(newSearchQuestionModuleMap);
      setQuestionList(quesList);
      qpg_settings_config?.allow_ai_questions &&
        qpg_search_questions_config?.prompt_validated === 'success' &&
        dispatch(
          setToastInfoV2({
            label: `${
              response.data?.questions?.length || 0
            } AI generated questions added to the question bank`,
            open: true,
            variant: 'info',
          })
        );
      setLoading('success');
    } catch (error) {
      setLoading('error');
    }
  };

  const filteredQuestionList = questionList.filter((q) => {
    return (
      (selectedDifficultyEnum
        ? q.questionMeta?.difficultyLevel === selectedDifficultyEnum
        : true) &&
      (selectedBloomLevel
        ? q.questionMeta?.bloomLevel === selectedBloomLevelEnum
        : true) &&
      (selectedQuestionTypeEnum
        ? q.questionType === selectedQuestionTypeEnum
        : true) &&
      (searchText
        ? q.questionText?.toLowerCase().includes(searchText.toLowerCase())
        : true)
      //    &&
      // (userFilter !== 'all'
      //   ? q.createdBy === user_info?.teacherProfileId
      //   : true)
    );
  });
  useEffect(() => {
    if (leftPanelScrollTopRef.current) {
      leftPanelScrollTopRef.current.scrollTop = 0;
    }
    if (rightPanelScrollTopRef.current) {
      rightPanelScrollTopRef.current.scrollTop = 0;
    }
  }, [selectedPage]);
  const updateFile = async (initialFile: CustomUploadFileResponse) => {
    setFile(initialFile);
  };

  const FileUpload = async (filesToUpload: FileList) => {
    const file = filesToUpload[0];
    console.log('file:', file, 'filesToUpload:', filesToUpload);
    if (!file) return;
    if (
      ![
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ].includes(file.type)
    ) {
      dispatch(
        setToastInfoV2({
          label: 'Please upload a valid Excel file (.xls or .xlsx)',
          variant: 'error',
          open: true,
        })
      );

      return;
    }

    const tempUserUploads: CustomUploadFileResponse = {
      uploadFileData: new CloudUpload({
        gcpFileUrl: '',
        personId: user_info?.teacherProfileId,
        fileSizeInMb: getFileSizeInMB(file.size),
        isDeleted: false,
        externalWebResourceUrl: '',
        fileName: file.name,
        fileType: getFileType(file.type),
        fileExtensionType: getFileExtensionType(file.type),
        isUploaded: false,
        signedUrl: '',
      }),
      fileUploadStatus: 'pending',
      fileSize: file.size,
    };

    await updateFile(tempUserUploads);

    //! max file size to be 50 MB
    if (getFileSizeInMB(file.size) > MAX_FILE_LIMIT_MB) {
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Size Exceeded';
      dispatch(
        setToastInfoV2({
          label: 'File Size should be less than 50 MB',
          variant: 'error',
          open: true,
        })
      );
      return;
    }
    setFileUploading(true);
    try {
      const signedUrlResponse: SignedUrlReponse =
        await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
          {
            personId: user_info?.teacherProfileId,
            filesInfo: [
              {
                originalFilePath: file.name,
                mimeType: MimeEnum.MIME_XLSX,
                size:
                  getFileSizeInMB(file.size) < 0.01
                    ? 0.01
                    : getFileSizeInMB(file.size),
              },
            ],
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_TEACHER_QUESTION,

            schoolId: user_info?.schoolDetails[0]?.schoolId || undefined,
          }
        );

      if (signedUrlResponse && signedUrlResponse.response.length > 0) {
        const signedUrl = getMediaBasePath(
          signedUrlResponse.response[0].signedUrl
        );
        const uploadSuccess = await uploadFile({ file, signedUrl });
        if (uploadSuccess.success === true) {
          tempUserUploads.uploadFileData.signedUrl = signedUrl;
          tempUserUploads.uploadFileData.isUploaded = true;
          tempUserUploads.fileUploadStatus = 'success';

          const response =
            await TeacherUploadQuestionV1ClientWithStatusCodeHandler.upsertTeacherQuestions(
              {
                gcsFilePath: signedUrl,
                schoolId: user_info?.schoolDetails[0]?.schoolId,
                moduleIds: moduleIds,
                teacherId: user_info?.teacherProfileId,
                subjectId: class_subject_info?.subjectId,
                sectionId: class_subject_info?.sectionId,
              }
            );

          if (response.status === APIResponse_StatusEnum.STATUS_SUCCESS) {
            tempUserUploads.uploadFileData.isUploaded = true;
            tempUserUploads.fileUploadStatus = 'success';
            setUploadStatusMessage({
              successMessage: response.message || 'File uploaded successfully',
            });
            fetchQuestions();
          } else {
            console.log(
              'response.validationErrors:',
              response.validationErrors
            );
            tempUserUploads.uploadFileData.isUploaded = false;
            tempUserUploads.fileUploadStatus = 'Upload Error';
            setUploadStatusMessage({ errors: response.validationErrors });
          }
        } else {
          tempUserUploads.uploadFileData.isUploaded = false;
          tempUserUploads.fileUploadStatus = 'Upload Error';
          dispatch(
            setToastInfoV2({
              label: `Error Occurred! Unable to upload the file.`,
              variant: 'error',
              open: true,
            })
          );
        }
        setFileUploading(false);
      } else {
        tempUserUploads.uploadFileData.isUploaded = false;
        tempUserUploads.fileUploadStatus = 'Upload Error';
        setUploadStatusMessage({ errors: ['Failed to get signed URL'] });
      }
    } catch (error: any) {
      console.log('error:', error);
      setFileUploading(false);
      tempUserUploads.uploadFileData.isUploaded = false;
      tempUserUploads.fileUploadStatus = 'Upload Error';
      setUploadStatusMessage({ errors: [formatErrorMessages(error.message)] });
    }
  };
  const downloadTemplate = async () => {
    // setTemplateDownloaded(true);
    setTemplateDownloading(true);
    try {
      dispatch(
        setToastInfoV2({
          label: `Downloading template.`,
          variant: 'success',
          open: true,
        })
      );

      const response =
        await TeacherUploadQuestionV1ClientWithStatusCodeHandler.downloadTeacherQuestionsTemplate(
          {
            subjectId: class_subject_info?.subjectId,
            teacherId: user_info?.teacherProfileId,
            sectionId: class_subject_info?.sectionId,
            schoolId: user_info?.schoolDetails[0]?.schoolId,
          }
        );

      console.log('response:', response);
      if (response.signedUrl) {
        if (getDeviceType() === 'android') {
          downloadPdfToAndroid(response.signedUrl);
        } else {
          const link = document.createElement('a');
          link.href = response.signedUrl;
          link.setAttribute('download', 'Question_Upload_Template.xlsx'); //or any other extension
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        setTemplateDownloading(true);
      } else {
        dispatch(
          setToastInfoV2({
            label: `Error Occurred! Unable to download template.`,
            variant: 'error',
            open: true,
          })
        );
      }

      setTemplateDownloading(false);
    } catch (error) {
      // console.log('error:', error);
      dispatch(
        setToastInfoV2({
          label: `Error Occurred! Unable to download template.`,
          variant: 'error',
          open: true,
        })
      );
      setTemplateDownloading(false);
    }
  };
  const handleDelete = async (questionId: string) => {
    try {
      setLoading('loading');
      await TeacherUploadQuestionV1ClientWithStatusCodeHandler.deleteTeacherQuestions(
        {
          teacherId: user_info?.teacherProfileId,
          schoolId: user_info?.schoolDetails[0]?.schoolId,
          questionIds: [questionId],
        }
      );
      dispatch(
        setToastInfoV2({
          label: 'Question deleted successfully',
          variant: 'info',
          open: true,
        })
      );
      setLoading('success');
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      dispatch(
        setToastInfoV2({
          label: 'Error deleting question',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
    }
  };

  useEffect(() => {
    setSelectedPage(1);
    setTotalPages(Math.ceil(filteredQuestionList.length / pageSize));
  }, [filteredQuestionList.length]);

  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [
    selectedChapterId,
    selectedTopicId,
    qpg_assessment_task?.subjectId,
    user_info?.teacherProfileId,
    selectedBloomLevel,
    selectedDifficulty,
    selectedQuestionType,
  ]);

  useEffect(() => {
    resetTopicId(selectedChapterId);
  }, [selectedChapterId, resetTopicId]);

  useEffect(() => {
    if (
      selectedQuestionType === 'Competency Based' &&
      qpg_settings_config?.allow_ai_questions
    ) {
      setSelectedQuestionType(null);
    }
  }, [qpg_settings_config?.allow_ai_questions]);

  // Helper function to build common API request parameters
  const buildModuleQuestionsParams = (
    moduleIds: number[],
    currentQuestion: Question,
    prompts?: string[],
    createdByUserId?: bigint
  ) => ({
    teacherId: user_info?.teacherProfileId,
    subjectId: qpgAssessmentTask.subjectId,
    schoolId: createdByUserId
      ? user_info?.schoolDetails[0]?.schoolId
      : undefined,
    moduleIds,
    assessmentCreationType:
      AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
    noOfQuestions: 1,
    createdByUserId,
    requiredQuestionTypes: selectedQuestionTypeEnum
      ? [selectedQuestionTypeEnum]
      : [],
    requiredDifficultyTypes: selectedDifficultyEnum
      ? [selectedDifficultyEnum]
      : [],
    bloomLevels: selectedBloomLevelEnum ? [selectedBloomLevelEnum] : [],
    qIdsToBeIgnored: [
      ...questionList.map((ques) => ques.questionId || ''),
      ...replacedQuestionIds,
    ],
    currentQuestionId: currentQuestion.questionId,
    allowAiQuestions: qpg_settings_config?.allow_ai_questions,
    aiQuestionPrompts: prompts || [],
    isAiAssessment: qpg_settings_config?.allow_ai_questions,
  });

  const handleReplaceAiQuestion = async (
    currentQuestion: Question,
    prompts?: string[]
  ) => {
    if (!currentQuestion) return;
    try {
      setLoading('loading');

      // Get all module IDs
      const allModuleIds =
        qpgAssessmentTask.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
          .flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];

      // Fetch replacement question
      const moduleQuestions =
        await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
          buildModuleQuestionsParams(allModuleIds, currentQuestion, prompts)
        );

      if (moduleQuestions?.data?.questions?.length) {
        const newQuestion = moduleQuestions.data.questions[0];

        // Track replaced questions
        if (
          currentQuestion.questionId &&
          !replacedQuestionIds.includes(currentQuestion.questionId)
        ) {
          setReplacedQuestionIds([
            ...replacedQuestionIds,
            currentQuestion.questionId,
          ]);
        }

        // Update question list by replacing the current question with the new one
        setQuestionList((prevList) =>
          prevList.map((q) =>
            q.questionId === currentQuestion.questionId ? newQuestion : q
          )
        );

        // Update search question module map
        const newQuestionModuleMapData =
          moduleQuestions.data?.questionModuleMap?.find(
            (val) => val.questionId === newQuestion.questionId
          );

        if (newQuestionModuleMapData) {
          setSearchQuestionModuleMap((prevMap) => [
            ...prevMap.filter(
              (val) => val.questionId !== currentQuestion.questionId
            ),
            newQuestionModuleMapData,
          ]);
        }

        dispatch(
          setToastInfoV2({
            label: 'Question replaced successfully',
            variant: 'info',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfoV2({
            label: 'No questions found to replace',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      let message = 'Failed to replace question';
      if (err instanceof ConnectError) {
        message = err.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      console.error('Error replacing AI question:', err);
      dispatch(
        setToastInfoV2({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setLoading('success');
    }
  };

  const backButtonClick = async () => {
    dispatch(setQuestionUploadFilter({ value: 'all' }));
    if (fromPath) {
      navigate(fromPath, {
        state: {
          isFromQuestionBank: true,
          redirectionPath: location.state?.redirectionPath,
        },
      });
    } else {
      navigate(-1);
    }
  };

  const renderSelectMenu = (
    optionList: string[],
    placeholder: string,
    value: string | null,
    onChange: (e: any, value: string | null) => void,
    onMobileClick: () => void,
    modalState: boolean,
    setModalState: (val: boolean) => void,
    disabled?: boolean
  ) => {
    if (isMobile) {
      return (
        <>
          <MobilePopup
            options={optionList}
            title={placeholder}
            modalState={modalState}
            setModalState={setModalState}
            onChange={onChange}
            clearall
          />
          <Box
            sx={{
              display: 'flex',
              height: {
                md: pxTovW(55),
                xs: pxToRem(38),
              },
              border: '1px solid #38FF92',
              borderRadius: '7px',
              boxSizing: 'border-box',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: pxToRem(10), md: pxTovW(20) },
              width: '100%',
              opacity: disabled ? 0.5 : 1,
            }}
            onClick={disabled ? undefined : onMobileClick}
          >
            <Typography
              // {...params}
              variant="h4"
              sx={{
                px: pxToRem(5),
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordBreak: 'break-word',
              }}
            >
              {value || placeholder}
            </Typography>
            <KeyboardArrowDownRoundedIcon
              sx={{
                color: 'text.disabled',
              }}
            />
          </Box>
        </>
      );
    }
    return (
      <Autocomplete
        disablePortal
        id="combo-box-demo"
        popupIcon={
          <KeyboardArrowDownRoundedIcon
            sx={{
              color: 'text.disabled',
              fontSize: { xs: 'small', md: 'large' },
            }}
          />
        }
        options={optionList}
        value={value}
        onChange={onChange}
        disabled={disabled}
        // disableClearable
        sx={{
          border: '1px solid #38FF92',
          borderRadius: '7px',
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: 'initial',
            '&:hover': {
              backgroundColor: 'initial',
            },
            '&:focus': {
              backgroundColor: 'initial',
            },
            '&:hover::before': {
              borderBottom: 'none',
            },
            '&:focus::before': {
              borderBottom: 'none',
            },
            '&::before': {
              borderBottom: 'none',
            },
            '&::after': {
              borderBottom: 'none',
            },
          },
        }}
        renderInput={(params) => (
          <TextField variant="filled" {...params} label={placeholder} />
        )}
      />
    );
  };

  const addQuestion = async (
    question: Question,
    sectionIndex?: number,
    questionIndex?: number
  ) => {
    await addOrReplaceQuestion(question, sectionIndex, questionIndex);
    const newQuestionModuleMapData = searchQuestionModuleMap.find(
      (val) => val.questionId === question.questionId
    );
    if (newQuestionModuleMapData) {
      dispatch(
        setQPGQuestionModuleMap([
          ...(qpg_question_module_map || []),
          newQuestionModuleMapData,
        ])
      );
    }
    await backButtonClick();
  };

  const handleAddQuestion = (question: Question) => {
    let sectionIndex = selected_section_index_for_alternate;
    const questionIndex = selected_question_index_for_alternate;
    // if not add alternate question, then check if question type exists in any section
    if (!isAlternateQuestion) {
      const questionFoundByType = qpgAssessmentQuestions.find(
        (e) => e.questionType === question.questionType
      );
      // if question found by type, then find the section index and add to particular section else create new section
      sectionIndex = questionFoundByType
        ? questionSectionMetaInfo.findIndex((e) =>
            e.sectionQuestionsInfo.find(
              (e) =>
                e.questionId === questionFoundByType.questionId ||
                e.alternateQuestions.some(
                  (altQuestion) =>
                    altQuestion.questionId === questionFoundByType.questionId
                )
            )
          )
        : undefined;
      if (typeof sectionIndex !== 'number') {
        const sectionMeta =
          qpgQuestionsSequenceInfo?.questionSectionMetaInfo || [];
        setPopupDetails({
          title:
            'Adding this question type will create a new section in your question paper',
          yesToast: `Section ${numberToAlphabet(
            sectionMeta.length + 1
          )} is added`,
          yesHandler: async () => {
            await addQuestion(question, sectionIndex, questionIndex);
          },
        });
        setPopupState(true);
        return;
      } else {
        setPopupDetails({
          title: `Adding this question will add the question to Section ${numberToAlphabet(
            sectionIndex + 1
          )}`,
          yesToast: `Question added to Section ${numberToAlphabet(
            sectionIndex + 1
          )} successfully`,
          yesHandler: async () => {
            await addQuestion(question, sectionIndex, questionIndex);
          },
        });
        setPopupState(true);
        return;
      }
    }
    setPopupDetails({
      title: `Choosing this will add an alternate question`,
      yesToast: `Question added successfully`,
      yesHandler: async () => {
        await addQuestion(question, sectionIndex, questionIndex);
      },
    });
    setPopupState(true);
  };

  if (loading === 'loading') {
    return <Loader />;
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heading}>
        {/* <Typography variant="h1">My Homework</Typography> */}
        <Box sx={styles.textBox}>
          <Typography variant="h2">Question Bank </Typography>
          <Typography variant="h3">
            <ChipBadge
              label={filteredQuestionList.length}
              color="primary"
              size="small"
            />
          </Typography>
        </Box>
        <Select
          value={question_upload_filter?.value}
          onChange={(e) => {
            dispatch(
              setQuestionUploadFilter({
                value: e.target.value as QuestionSourceFilter,
              })
            );
            fetchQuestions(undefined, e.target.value as QuestionSourceFilter);
          }}
          displayEmpty
          renderValue={() => {
            if (!question_upload_filter?.value) {
              return <Typography color="text.disabled">Select</Typography>;
            }
            if (question_upload_filter?.value === 'all') {
              return <Typography variant="cardText"> All Questions</Typography>;
            }
            if (question_upload_filter?.value === 'text-book-questions') {
              return (
                <Typography variant="cardText"> Textbook Questions</Typography>
              );
            }
            return (
              <Typography variant="cardText">
                {' '}
                Teacher Uploaded Questions
              </Typography>
            );
          }}
          sx={{
            background: '#F5FAFF',
            width: { xs: '100%', md: pxTovW(332) },
            borderRadius: { xs: pxToRem(10), md: pxTovW(5) },
            height: { xs: 'max-content', md: '70%', lg: pxTovW(65) },
            '.MuiOutlinedInput-notchedOutline': { border: '2px solid #87C5FF' },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '2px solid #87C5FF',
            },
            boxShadow: 'none',
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: { xs: pxToRem(100), md: pxTovW(300) },
                minHeight: { xs: 'max-content', md: 'max-content' },
                backgroundColor: '#F8FCFF',
                '&::-webkit-scrollbar': { display: 'none' },
                padding: 0,
                borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                '& .MuiList-root': {
                  backgroundColor: '#F8FCFF',
                  maxHeight: { xs: pxToRem(150), md: pxTovW(300) },
                  padding: 0,
                },

                minWidth: { xs: '95%', md: pxTovW(230) },
                maxWidth: { xs: '95%', md: pxTovW(200), lg: pxTovW(150) },
              },
            },
          }}
        >
          <MenuItem value="all">All Questions</MenuItem>
          <MenuItem value="teacher">Teacher Uploaded Questions</MenuItem>
          {isTextBookQuestionEnabled && (
            <MenuItem value="text-book-questions">Textbook Questions</MenuItem>
          )}
        </Select>
      </Box>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel} ref={leftPanelScrollTopRef}>
          <Box sx={styles.header}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: pxToRem(10), md: pxTovW(20) },
                backgroundColor: 'common.white',
                borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
                boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: pxToRem(10), md: pxTovW(20) },
                  paddingBottom: { xs: pxToRem(10), md: pxTovW(20) },
                  marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
                }}
              >
                <TextField
                  variant="outlined"
                  fullWidth
                  onChange={(e) => setSearchText(e.target.value)}
                  // onKeyDown={(e) => e.key === 'Enter' && console.log('Search')}
                  value={searchText}
                  placeholder="Search concepts or subtopics"
                  sx={{
                    border: '1px solid #007CDC',
                    borderRadius: {
                      xs: pxToRem(8),
                      md: pxTovW(15),
                    },
                    '& .MuiOutlinedInput-root': {
                      fontWeight: '700',
                      color: 'black',
                      px: 0,
                      p: {
                        xs: pxToRem(8),
                        md: `${pxToRem(15)} ${pxToRem(20)}`,
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (searchText === '' ||
                      searchText === null) && (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{
                            fontSize: {
                              xs: pxToRem(18),
                              md: pxToRem(25),
                            },
                          }}
                          color="primary"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    difficultyValuesArray,
                    'Difficulty Level',
                    selectedDifficulty ?? null,
                    async (e, value) => {
                      setSelectedDifficulty(value);
                      value &&
                        (await interactionEvent({
                          context: 'Difficulty_level',
                          name: value.toUpperCase().replace(/\s+/g, '_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setDifficultyModalState(true),
                    difficultyModalState,
                    setDifficultyModalState
                  )}
                  {renderSelectMenu(
                    availableQuestionTypes,
                    'Question Type',
                    selectedQuestionType ?? null,
                    async (e, value) => {
                      setSelectedQuestionType(value);
                      value &&
                        (await interactionEvent({
                          context: 'Question_type',
                          name: value
                            .toUpperCase()
                            .replace(/\s+/g, '_')
                            .replace(/\//g, '_/_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setQuesTypeModalState(true),
                    quesTypeModalState,
                    setQuesTypeModalState,
                    !!selected_question_type_for_alternate
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  {renderSelectMenu(
                    bloomLevelValuesArray,
                    'Bloom Level',
                    selectedBloomLevel ?? null,
                    async (e, value) => {
                      setSelectedBloomLevel(value);
                      value &&
                        (await interactionEvent({
                          context: 'Bloom_level',
                          name: value.toUpperCase().replace(/\s+/g, '_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        }));
                    },
                    () => setBloomLevelModalState(true),
                    bloomLevelModalState,
                    setBloomLevelModalState
                  )}
                  {/* empty box so that above box takes 50% width */}
                  <Box sx={{ width: '100%' }} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: pxToRem(10), md: pxTovW(20) },
                    borderTop: '1px dashed #E3E3E3',
                    pt: { xs: pxToRem(10), md: pxTovW(20) },
                  }}
                >
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Chapter
                    </Typography>
                    {renderSelectMenu(
                      chapterOptions.map((e) => e.label),
                      'Chapter',
                      chapterOptions.find(
                        (e) => e.chapterId === selectedChapterId
                      )?.label ?? null,
                      async (e, value) => {
                        setSelectedChapterId(
                          chapterOptions.find((e) => e.label === value)
                            ?.chapterId || null
                        );
                        value &&
                          (await interactionEvent({
                            context: 'Search_by_chapter',
                            name: value
                              .toUpperCase()
                              .replace(/\s+/g, '_')
                              .replace(/[:.]/g, ''),
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          }));
                      },
                      () => setChapterModalState(true),
                      chapterModalState,
                      setChapterModalState
                    )}
                  </Box>
                  <Box sx={styles.selectContainer}>
                    <Typography variant="h4" fontWeight={'bold'}>
                      Search by Topic
                    </Typography>
                    {renderSelectMenu(
                      topicOptions.map((e) => e.label),
                      'Topic',
                      topicOptions.find((e) => e.topicId === selectedTopicId)
                        ?.label ?? null,
                      async (e, value) => {
                        setSelectedTopicId(
                          topicOptions.find((e) => e.label === value)
                            ?.topicId || null
                        );
                        value &&
                          (await interactionEvent({
                            context: 'Search_by_topic',
                            name: value
                              .toUpperCase()
                              .replace(/\s+/g, '_')
                              .replace(/[:.]/g, ''),
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          }));
                      },
                      () => setTopicModalState(true),
                      topicModalState,
                      setTopicModalState
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: { xs: pxToRem(10), md: pxTovW(20) },
                    }}
                  >
                    {(!!qpg_settings_config?.allow_ai_questions ||
                      !!qpg_assessment_task?.isAiAssessment) && (
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            p: {
                              xs: `${pxToRem(8)} ${pxToRem(10)}`,
                              md: `${pxTovW(10)} ${pxTovW(12)}`,
                            },
                            borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                            border: '1px solid #0AA34F',
                            cursor: 'pointer',
                            background: '#CFFFE5',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: pxToRem(5), md: pxTovW(10) },
                          }}
                          onClick={() => {
                            setAddPromptPopupOpen(true);
                          }}
                        >
                          <IconWrapper
                            name="plus-green"
                            customSx={{ height: '23px', width: '23px' }}
                            type="png"
                            parentFolder="icons"
                          />
                          <Typography
                            variant="cardText"
                            color={'#0AA34F'}
                            fontWeight={'600'}
                          >
                            Add Questions with AI
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          p: {
                            xs: `${pxToRem(8)} ${pxToRem(10)}`,
                            md: `${pxTovW(10)} ${pxTovW(12)}`,
                          },
                          borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                          border: '1px solid #0AA34F',
                          cursor: 'pointer',
                          background: '#CFFFE5',
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                        onClick={() => {
                          setOpenFileUploadPopup(true);
                        }}
                      >
                        <IconWrapper
                          name="blue-plus"
                          customSx={{ height: '23px', width: '23px' }}
                          type="png"
                          parentFolder="icons"
                        />
                        <Typography
                          variant="cardText"
                          color={'#007CDC'}
                          fontWeight={'600'}
                        >
                          Add Your Own Questions
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={styles.rightPanel} ref={rightPanelScrollTopRef}>
          {filteredQuestionList.length > 0 ? (
            filteredQuestionList
              .slice((selectedPage - 1) * pageSize, selectedPage * pageSize)
              .map((q, index) => (
                <Box key={q.questionId || `ques_fallback_${index}`}>
                  <QuestionContainerWithSolution
                    question={q}
                    hideMarks
                    showQuestionStats
                    showAnswer
                    imageResizeEnabled={true}
                    key={'ques_' + index}
                    headerLeftElement={
                      <>
                        {q.isAiGenerated ? (
                          <AiGeneratedButton
                            handlePrompt={(prompt) => {
                              handleReplaceAiQuestion(q, [prompt]);
                            }}
                          />
                        ) : null}
                        <QuestionSourceTags
                          question={q}
                          teacherProfileId={user_info?.teacherProfileId}
                          teacherName={user_info?.userName}
                        />
                      </>
                    }
                    headerRightElement={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                      >
                        <Button
                          sx={{ marginLeft: 'auto' }}
                          onClick={async () => {
                            handleAddQuestion(q);
                            await interactionEvent({
                              context: 'Question_bank',
                              name: 'ADD',
                              pathSegments: pathSegments,
                              isOffline: isOffline,
                            });
                          }}
                        >
                          <QuantityButton
                            buttonText={isAlternateQuestion ? 'Choose' : 'Add'}
                            quantity={0}
                            maxQuantity={1}
                          />
                        </Button>
                        {q.createdBy === user_info?.teacherProfileId ? (
                          <IconWrapper
                            name={'deletehw'}
                            size="md"
                            type="png"
                            parentFolder="icons"
                            customSx={{
                              width: {
                                xs: pxToRem(17),
                                md: pxTovW(35),
                                lg: pxTovW(24),
                              },
                              margin: { xs: pxToRem(10), md: 0 },
                            }}
                            onClick={() => handleDelete(q.questionId || '')}
                          />
                        ) : (
                          <></>
                        )}
                      </Box>
                    }
                  />
                </Box>
              ))
          ) : (
            <NoContentCard
              variant="info"
              icon="cards"
              text="No Questions Available"
            />
          )}
          {totalPages > 1 && (
            <Pagination
              page={selectedPage}
              count={totalPages}
              color="primary"
              onChange={async (
                event: React.ChangeEvent<unknown>,
                page: number
              ) => {
                setSelectedPage(page);
              }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'fixed',
                bottom: 0,
                left: { xs: pxToRem(10), md: 'unset' },
                border: '1px solid #007CDC',
                borderTopLeftRadius: { xs: pxToRem(10), md: pxTovW(16) },
                borderTopRightRadius: { xs: pxToRem(10), md: pxTovW(16) },
                boxShadow: '0px 0px 10px #00000029',

                width: { xs: '95%', md: pxTovW(950), lg: pxTovW(840) },
                height: { xs: pxToRem(55), md: pxTovW(85) },
                marginX: 'auto',
                mb: pxTovW(10),
                backgroundColor: '#E9F5FF',
              }}
            />
          )}
        </Box>
      </Box>

      <EditWarningPopup
        open={popupState}
        handleClose={() => {
          setPopupState(false);
        }}
        yesClickHandler={async () => {
          if (popupDetails.yesHandler) {
            await popupDetails.yesHandler();
          }
          if (popupDetails.yesToast) {
            dispatch(
              setToastInfoV2({
                label: popupDetails.yesToast,
                open: true,
                variant: 'info',
              })
            );
          }
          setPopupState(false);
        }}
        noClickHandler={() => {
          setPopupState(false);
        }}
        iconname={'correct'}
        text1={popupDetails.title || ''}
        text2={'Would you like to proceed?'}
      />

      <AddPromptPopup
        disabled={
          !qpg_search_questions_config?.prompt ||
          qpg_search_questions_config?.prompt_validated !== 'success'
        }
        open={addPromptPopupOpen}
        value={qpg_search_questions_config?.prompt}
        onChange={(val) => {
          dispatch(
            setQPGSearchQuestionsConfig({
              ...qpg_search_questions_config,
              prompt: val,
              prompt_validated: undefined,
            })
          );
        }}
        handleClose={() => {
          dispatch(setQPGSearchQuestionsConfig(undefined));
          setAddPromptPopupOpen(false);
        }}
        addQuestionsHandler={() => {
          fetchQuestions();
          dispatch(
            setQPGSearchQuestionsConfig({
              ...qpg_search_questions_config,
              prompt: undefined,
              prompt_validated: undefined,
            })
          );
          setAddPromptPopupOpen(false);
        }}
        validationEnabled={
          !!qpg_settings_config?.allow_ai_questions ||
          !!qpg_assessment_task?.isAiAssessment
        }
        validated={qpg_search_questions_config?.prompt_validated}
        onValidateClick={async () => {
          if (!qpg_search_questions_config?.prompt) {
            return;
          }
          try {
            setLoading('loading');
            const res =
              await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.checkValidPrompt(
                {
                  subject:
                    getTeacherSubjectName(
                      chapter_topic_data?.subjectId,
                      user_info?.teachClassSubjects
                    ) || '',
                  moduleIds,
                  bloomLevels: qpg_settings_config?.bloomLevels || [],
                  questionSectionInfo:
                    current_qpg_template_info?.questionSectionInfo || [],

                  avgDifficultyLevel: getDifficultyLevelFromString(
                    qpg_settings_config?.overallDifficultyLevel || 'Medium'
                  ),
                  customPrompt: qpg_search_questions_config?.prompt,
                  teacherId: user_info?.teacherProfileId,
                  assessmentCreationType:
                    AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
                }
              );
            if (res.isValidated) {
              dispatch(
                setQPGSearchQuestionsConfig({
                  ...qpg_search_questions_config,
                  prompt_validated: 'success',
                })
              );
              setValidationStatus({
                type: 'success',
                message: 'Prompt validated! Ready to use.',
              });
              setAddPromptPopupOpen(false);
            } else {
              dispatch(
                setQPGSearchQuestionsConfig({
                  ...qpg_search_questions_config,
                  prompt_validated: 'error',
                })
              );
              setValidationStatus({
                type: 'error',
                heading: 'Invalid Prompt',
                message: (
                  (res.message || '') + ' Please review and try again.'
                ).trim(),
              });
              setAddPromptPopupOpen(false);
            }
          } catch (err) {
            console.log(err);
            setValidationStatus({
              type: 'error',
              heading: 'Some error occurred',
              message:
                'Error while validating prompt. Please try after some time',
            });
            setAddPromptPopupOpen(false);
          } finally {
            setLoading('success');
          }
        }}
      />

      <ValidationPopup
        open={!!validationStatus}
        handleClose={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        okHandler={() => {
          setValidationStatus(undefined);
          setAddPromptPopupOpen(true);
        }}
        iconname={
          validationStatus?.type == 'success'
            ? 'check-green'
            : validationStatus?.type == 'error'
            ? 'wrong'
            : undefined
        }
        text1={validationStatus?.heading}
        text2={validationStatus?.message}
      />

      <AddQuestionsPopup
        open={openFileUploadPopup}
        handleClose={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
          setOpenFileUploadPopup(false);
        }}
        retryUploadFunction={() => {
          setFileUploading(false);
          setFile(undefined);
          setUploadStatusMessage({});
        }}
        downloadClickHandler={downloadTemplate}
        templateDownloading={templateDownloading}
        instructions={[
          'Download the question template from the DownLoad Template button',
          'Fill in your questions following the format specified in the template',
          'Ensure all required fields are completed (question text, options, correct answer, difficulty level)',
          'Save the file in .xlsx or .csv format',
          'Upload the completed file using the upload section below',
          'Review the uploaded questions before adding them to your question bank',
        ]}
        userUploadedFile={File}
        FileUploading={FileUploading}
        handleChangeFunction={(event) => {
          FileUpload(event.target.files as FileList);
          event.target.value = '';
        }}
        uploadStatusMessage={uploadStatusMessage}
        setUploadStatusMessage={setUploadStatusMessage}
      />
    </Box>
  );
}
